import React, { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import {
  createSectionWorkType,
  getKnownWorkTypeNames,
} from "../../../../api/projectProgressApi";
import "./AddWorkTypeModal.css";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    borderColor: state.isFocused ? "#2a5e38" : "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "#2a5e38" : "#9ca3af" },
  }),
  multiValue: (provided) => ({
    ...provided,
    background: "#eef5f0",
    borderRadius: 8,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#214a2c",
    fontSize: 13,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#6b7280",
    ":hover": { background: "#d8ebe0", color: "#214a2c" },
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1100,
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected
      ? "#2a5e38"
      : state.isFocused
        ? "#eef5f0"
        : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
  }),
};

const AddWorkTypeModal = ({
  open,
  projectId,
  sectionId,
  existingNames = [],
  onClose,
  onCreated,
}) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setSubmitting(false);
    setSubmitError("");
    setLoadError("");
    setOptions([]);

    let cancelled = false;
    (async () => {
      try {
        setLoadingNames(true);
        const data = await getKnownWorkTypeNames(projectId, sectionId, {
          isMilestone: false,
        });
        if (cancelled) return;
        const names = Array.isArray(data?.names) ? data.names : [];
        const existingSet = new Set(existingNames.map(normalizeName));
        setOptions(
          names
            .filter((name) => name && !existingSet.has(normalizeName(name)))
            .map((name) => ({ value: name, label: name })),
        );
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Не удалось загрузить список видов работ");
        }
      } finally {
        if (!cancelled) setLoadingNames(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, projectId, sectionId, existingNames]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onClose]);

  const existingSet = useMemo(
    () => new Set(existingNames.map(normalizeName)),
    [existingNames],
  );

  const canSubmit = selected.length > 0 && !submitting;

  if (!open) return null;

  const isValidNewOption = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (!trimmed) return false;
    const key = normalizeName(trimmed);
    if (existingSet.has(key)) return false;
    if (selected.some((opt) => normalizeName(opt.value) === key)) return false;
    if (options.some((opt) => normalizeName(opt.value) === key)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setSubmitError("");

      const results = [];
      const errors = [];

      for (const opt of selected) {
        const name = String(opt.value || "").trim();
        if (!name) continue;
        try {
          const created = await createSectionWorkType(projectId, sectionId, {
            name,
            is_milestone: false,
            unit_weight_percent: 0,
          });
          results.push(created);
        } catch (err) {
          errors.push(`${name}: ${err?.message || "ошибка"}`);
        }
      }

      if (results.length > 0) {
        onCreated?.(results);
      }

      if (errors.length > 0) {
        setSubmitError(
          results.length > 0
            ? `Часть видов работ добавлена. Ошибки: ${errors.join("; ")}`
            : errors.join("; "),
        );
        if (results.length > 0) {
          // оставляем только неудавшиеся в селекте
          const failed = new Set(
            errors.map((msg) => normalizeName(msg.split(":")[0])),
          );
          setSelected((prev) =>
            prev.filter((opt) => failed.has(normalizeName(opt.value))),
          );
        }
        return;
      }

      onClose();
    } catch (err) {
      setSubmitError(err?.message || "Не удалось добавить вид работ");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="add-work-type-modal-backdrop"
        onClick={() => !submitting && onClose()}
      />
      <div
        className="add-work-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-work-type-modal-title"
      >
        <h2 id="add-work-type-modal-title" className="add-work-type-modal__title">
          Добавление нового вида работ
        </h2>

        <div className="add-work-type-modal__hint">
          <span className="add-work-type-modal__hint-icon" aria-hidden>
            ⚠
          </span>
          <p>
            После добавления укажите вес нового вида работ и проверьте, чтобы
            сумма весов по разделу была 100%.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="add-work-type-modal__label" htmlFor="add-work-type-select">
            Выберите виды работ или создайте новый
          </label>

          <CreatableSelect
            inputId="add-work-type-select"
            isMulti
            isClearable
            isSearchable
            isDisabled={submitting}
            isLoading={loadingNames}
            options={options}
            value={selected}
            onChange={(value) => {
              setSelected(value || []);
              setSubmitError("");
            }}
            styles={selectStyles}
            menuPortalTarget={document.body}
            placeholder="Начните вводить название..."
            noOptionsMessage={() =>
              loadingNames
                ? "Загрузка..."
                : "Нет вариантов — введите название, чтобы создать"
            }
            formatCreateLabel={(input) => `Создать «${input.trim()}»`}
            isValidNewOption={isValidNewOption}
            onCreateOption={(inputValue) => {
              const name = String(inputValue || "").trim();
              if (!isValidNewOption(name)) {
                if (existingSet.has(normalizeName(name))) {
                  setSubmitError("Такой вид работ уже есть в этом разделе!");
                }
                return;
              }
              const option = { value: name, label: name, __isNew__: true };
              setOptions((prev) => [...prev, option]);
              setSelected((prev) => [...prev, option]);
              setSubmitError("");
            }}
          />

          {loadError && (
            <p className="add-work-type-modal__error">{loadError}</p>
          )}
          {submitError && (
            <p className="add-work-type-modal__error">{submitError}</p>
          )}

          <div className="add-work-type-modal__actions">
            <button
              type="button"
              className="add-work-type-modal__btn add-work-type-modal__btn--cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-work-type-modal__btn add-work-type-modal__btn--submit"
              disabled={!canSubmit}
            >
              {submitting
                ? "Добавление..."
                : selected.length > 1
                  ? `Добавить (${selected.length})`
                  : "Добавить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddWorkTypeModal;
